import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import omit from "lodash/omit";
import { ComponentProps } from "react";
import { OrgScopedBaseUser } from "src/stores/models/user";
import { QualityEventDraft, qualityEventRows } from "src/types/quality-events";
import { QualityEventStepValues } from "./QualityEventStepValues";

const NO_EXISTING_FILES: { id: string; filename: string }[] = [];

export const QualityEventDraftForm = ({
  draft,
  users,
  formId,
  pending,
  error,
  existingFiles = NO_EXISTING_FILES,
  onDraftChange,
  ...stepValuesProps
}: Omit<ComponentProps<typeof QualityEventStepValues>, "files" | "edit"> & {
  draft: QualityEventDraft;
  users: OrgScopedBaseUser[];
  formId: string;
  pending: boolean;
  error: string | null;
  existingFiles?: { id: string; filename: string }[];
  onDraftChange?: (draft: QualityEventDraft) => void;
}) => {
  const { step, record } = stepValuesProps;
  const disabled = pending || !onDraftChange;
  const updateDraft = (changes: Partial<QualityEventDraft>) =>
    onDraftChange?.({ ...draft, ...changes });
  const orphanedFileGroups = Object.entries(draft.files).flatMap(
    ([key, files]) => {
      const [group, rowId] = key.split(":");
      const activeRow =
        rowId === undefined ||
        qualityEventRows(draft.answers[group]).some((row) => row.id === rowId);
      return files.length > 0 && !activeRow
        ? [{ key, names: files.map((file) => file.name).join(", ") }]
        : [];
    }
  );
  return (
    <Box
      component="form"
      id={formId}
      noValidate
      aria-label={`${step.title} fields`}
      className="flex flex-col gap-[16px] [container-type:inline-size]"
      sx={
        onDraftChange
          ? undefined
          : (theme) => ({
              "& .MuiInputBase-input.Mui-disabled": {
                color: theme.palette.text.primary,
                WebkitTextFillColor: theme.palette.text.primary,
              },
              "& .MuiFormControlLabel-label.Mui-disabled, & .MuiPickersInputBase-root.Mui-disabled":
                {
                  color: theme.palette.text.primary,
                },
            })
      }
      onSubmit={(event) => event.preventDefault()}
    >
      {step.description && (
        <Typography variant="body1" color="text.primary">
          {step.description}
        </Typography>
      )}
      {(!record || step.key === record.definition.definition.steps[0].key) && (
        <TextField
          label="Title"
          className="w-full self-start [@container(min-width:640px)]:w-[calc((100%-16px)/2)]"
          size="small"
          value={draft.title}
          disabled={disabled}
          onChange={(event) => updateDraft({ title: event.target.value })}
        />
      )}
      <QualityEventStepValues
        {...stepValuesProps}
        files={existingFiles}
        pending={pending}
        users={users}
        edit={{ draft, onChange: onDraftChange }}
      />
      {orphanedFileGroups.map(({ key, names }) => (
        <Alert
          key={key}
          severity="warning"
          action={
            <Button
              color="inherit"
              disabled={disabled}
              onClick={() =>
                updateDraft({
                  files: omit(draft.files, key),
                })
              }
            >
              Remove evidence
            </Button>
          }
        >
          These files belong to an action removed from the proposal: {names}.
          Review them before saving.
        </Alert>
      ))}
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
};
